import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  generatePatientReportPdf,
  generateFinanceReportPdf,
  generateTreatmentsReportSummary,
  generateTreatmentsSummaryPdf,
  generateTreatmentPdf,
  getGeneratedReports,
  getGeneratedReport,
  getTreatmentsInRange,
} from "../../firebase";

/**
 * patientReportsApi — generated patient report PDFs (wizard on the Reports
 * page). Generation runs server-side (Cloud Function renders + stores the
 * PDF and writes a generatedReports history doc); the history list is a
 * plain fetch.
 *
 * Error codes: CK-7990 (history), CK-7991 (generate), CK-7992 (treatments in
 * range), CK-7993 (AI treatments summary), CK-7994 (treatments summary PDF),
 * CK-7995 (finance report PDF), CK-7967 (single treatment PDF), CK-7997
 * (single history row for re-edit).
 */
export const patientReportsApi = createApi({
  reducerPath: "patientReportsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["GeneratedReports"],
  endpoints: (builder) => ({
    getGeneratedReports: builder.query({
      async queryFn({ clinicId, limit = 20, createdBy = null }) {
        try {
          const reports = await getGeneratedReports(clinicId, { limit, createdBy });
          return { data: reports };
        } catch (error) {
          console.error("[CK-7990] Error fetching generated reports:", {
            errorCode: "CK-7990",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "GeneratedReports", id: "LIST" }],
    }),

    // One history row — seeds the AI summary page when reopened for editing
    getGeneratedReport: builder.query({
      async queryFn({ clinicId, reportId }) {
        try {
          const report = await getGeneratedReport(clinicId, reportId);
          return { data: report };
        } catch (error) {
          console.error("[CK-7997] Error fetching generated report:", {
            errorCode: "CK-7997",
            errorMessage: error.message,
            clinicId,
            reportId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      providesTags: (result, error, { reportId }) => [{ type: "GeneratedReports", id: reportId }],
    }),

    // Treatments in range, clinic-wide — the "all patients overview" report
    getTreatmentsInRange: builder.query({
      async queryFn({ clinicId, start, end, therapistId }) {
        try {
          const treatments = await getTreatmentsInRange(clinicId, {
            start,
            end,
            therapistId,
          });
          return { data: treatments };
        } catch (error) {
          console.error("[CK-7992] Error fetching treatments in range:", {
            errorCode: "CK-7992",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
    }),

    generatePatientReportPdf: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await generatePatientReportPdf(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7991] Error generating patient report PDF:", {
            errorCode: "CK-7991",
            errorMessage: error.message,
            clinicId: payload?.clinicId,
            reportTypeId: payload?.reportTypeId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "GeneratedReports", id: "LIST" }],
    }),

    // AI draft for the "סיכום טיפולים AI" report — returns editable HTML
    generateTreatmentsReportSummary: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await generateTreatmentsReportSummary(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7993] Error generating AI treatments summary:", {
            errorCode: "CK-7993",
            errorMessage: error.message,
            clinicId: payload?.clinicId,
            treatmentCount: payload?.treatmentIds?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
    }),

    generateFinanceReportPdf: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await generateFinanceReportPdf(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7995] Error generating finance report PDF:", {
            errorCode: "CK-7995",
            errorMessage: error.message,
            clinicId: payload?.clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [{ type: "GeneratedReports", id: "LIST" }],
    }),

    generateTreatmentsSummaryPdf: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await generateTreatmentsSummaryPdf(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7994] Error generating treatments summary PDF:", {
            errorCode: "CK-7994",
            errorMessage: error.message,
            clinicId: payload?.clinicId,
            treatmentCount: payload?.treatmentIds?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [{ type: "GeneratedReports", id: "LIST" }],
    }),

    // One locked treatment → branded PDF (patient page › treatments › ייצוא PDF)
    generateTreatmentPdf: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await generateTreatmentPdf(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7967] Error generating treatment PDF:", {
            errorCode: "CK-7967",
            errorMessage: error.message,
            clinicId: payload?.clinicId,
            treatmentId: payload?.treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [{ type: "GeneratedReports", id: "LIST" }],
    }),
  }),
});

export const {
  useGenerateTreatmentPdfMutation,
  useGetGeneratedReportQuery,
  useGetGeneratedReportsQuery,
  useGenerateFinanceReportPdfMutation,
  useGetTreatmentsInRangeQuery,
  useGeneratePatientReportPdfMutation,
  useGenerateTreatmentsReportSummaryMutation,
  useGenerateTreatmentsSummaryPdfMutation,
} = patientReportsApi;
